import { useEffect, useState, useCallback } from 'react';
import { useSessionStore, useIsRehearsalMode } from '@/state/session-store';
import { TimeHealthBar } from './TimeHealthBar';
import { TalkRatioBar, calculateTalkRatio } from './TalkRatioBar';
import { WPMIndicator, calculateWPM } from './WPMIndicator';
import { Reminders } from './Reminders';
import { ActionItems } from './ActionItems';

interface Props {
  onAlert?: (message: string) => void;
}

export function CoachingPanel({ onAlert }: Props) {
  const {
    sessionTiming,
    reminders,
    actionItems,
    coachingAlerts,
    coachingSettings,
    talkRatio,
    wpmSegments,
    speakerTalkTime,
    toggleReminder,
    deleteReminder,
    toggleActionItem,
    deleteActionItem,
    addCoachingAlert,
    dismissCoachingAlert,
    swapUserSpeaker,
    getElapsedTime,
    status,
  } = useSessionStore();

  const isRehearsalMode = useIsRehearsalMode();
  const [elapsed, setElapsed] = useState(0);
  const [talkRatioAlertShown, setTalkRatioAlertShown] = useState(false);
  const [wpmAlertShown, setWpmAlertShown] = useState(false);

  // Update elapsed time every second
  useEffect(() => {
    if (status !== 'listening') return;
    const interval = setInterval(() => {
      setElapsed(getElapsedTime());
    }, 1000);
    setElapsed(getElapsedTime());
    return () => clearInterval(interval);
  }, [status, getElapsedTime]);

  // Calculate current metrics
  const talkRatioPct = calculateTalkRatio(talkRatio.userTalkTimeMs, talkRatio.otherTalkTimeMs);
  const wpm = calculateWPM(wpmSegments);
  const speakerCount = Object.keys(speakerTalkTime).length;

  // Talk ratio alert - skip in rehearsal mode (user is talking to computer)
  useEffect(() => {
    if (isRehearsalMode) return; // No talk ratio alerts in rehearsal mode

    if (talkRatioPct > coachingSettings.talkRatioThreshold && !talkRatioAlertShown) {
      const message = `You're talking ${talkRatioPct}% of the time. Let them talk!`;
      addCoachingAlert({ type: 'talk_ratio', timestamp: Date.now(), message, value: talkRatioPct });
      onAlert?.(message);
      setTalkRatioAlertShown(true);
    }
    // Reset alert flag when ratio improves
    if (talkRatioPct <= coachingSettings.talkRatioThreshold - 10) {
      setTalkRatioAlertShown(false);
    }
  }, [talkRatioPct, coachingSettings.talkRatioThreshold, talkRatioAlertShown, addCoachingAlert, onAlert, isRehearsalMode]);

  // WPM alert
  useEffect(() => {
    if (wpm > coachingSettings.wpmThreshold && !wpmAlertShown) {
      const message = `Slow down! You're speaking at ${wpm} WPM.`;
      addCoachingAlert({ type: 'wpm', timestamp: Date.now(), message, value: wpm });
      onAlert?.(message);
      setWpmAlertShown(true);
    }
    // Reset alert flag when pace slows
    if (wpm <= coachingSettings.wpmThreshold - 20) {
      setWpmAlertShown(false);
    }
  }, [wpm, coachingSettings.wpmThreshold, wpmAlertShown, addCoachingAlert, onAlert]);

  // Time alert handlers
  const handleHalfwayAlert = useCallback(() => {
    const message = "Halfway point - have you started discovery?";
    addCoachingAlert({ type: 'time', timestamp: Date.now(), message });
    onAlert?.(message);
  }, [addCoachingAlert, onAlert]);

  const handleFiveMinAlert = useCallback(() => {
    const message = "5 minutes left - wrap up and set next steps";
    addCoachingAlert({ type: 'time', timestamp: Date.now(), message });
    onAlert?.(message);
  }, [addCoachingAlert, onAlert]);

  // Get active (non-dismissed) alerts
  const activeAlerts = coachingAlerts.filter(a => !a.dismissed);

  return (
    <div className="space-y-4">
      {/* Active Alerts */}
      {activeAlerts.length > 0 && (
        <div className="space-y-2">
          {activeAlerts.slice(-3).map(alert => (
            <div
              key={alert.id}
              className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                alert.type === 'time' ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' :
                alert.type === 'talk_ratio' ? 'bg-orange-50 text-orange-800 border border-orange-200' :
                'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              <span>{alert.message}</span>
              <button
                onClick={() => dismissCoachingAlert(alert.id)}
                className="text-gray-400 hover:text-gray-600 ml-2"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Time Health Bar */}
      {sessionTiming && (
        <TimeHealthBar
          elapsedMs={elapsed}
          totalDurationMin={sessionTiming.scheduledDurationMin}
          halfwayEnabled={sessionTiming.alerts.halfway}
          fiveMinEnabled={sessionTiming.alerts.fiveMinWarning}
          onHalfway={handleHalfwayAlert}
          onFiveMinWarning={handleFiveMinAlert}
        />
      )}

      {/* Metrics Row */}
      <div className="flex items-center justify-between gap-4">
        {/* Talk Ratio - hidden in rehearsal mode since user is talking to computer */}
        {!isRehearsalMode && (
          <div className="flex-1">
            <TalkRatioBar
              userPct={talkRatioPct}
              threshold={coachingSettings.talkRatioThreshold}
              speakerCount={speakerCount}
              onSwapSpeaker={swapUserSpeaker}
            />
          </div>
        )}
        <WPMIndicator wpm={wpm} threshold={coachingSettings.wpmThreshold} />
      </div>

      {/* Reminders */}
      {reminders.length > 0 && (
        <div className="border-t border-gray-100 pt-3">
          <Reminders
            reminders={reminders}
            onToggle={toggleReminder}
            onDelete={deleteReminder}
            readOnly={true}
          />
        </div>
      )}

      {/* Action Items */}
      {actionItems.length > 0 && (
        <div className="border-t border-gray-100 pt-3">
          <ActionItems
            items={actionItems}
            onToggle={toggleActionItem}
            onDelete={deleteActionItem}
          />
        </div>
      )}
    </div>
  );
}
